import { AudioInputTest, testAudioInputDevice } from "@twilio/rtc-diagnostics";
import { useIsMountedRef } from "hooks/useIsMountedRef";
import React, { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { AppDispatch } from "store/store";
import { toggleAudioTestDialog } from "store/user/slice";
import { Icon } from "../icon/Icon";
import { AudioLevelMeter } from "../preview/AudioLevelMeter";
import styles from "./AudioTestDialog.module.css";

enum RecordingState {
  NotStarted,
  InProgress,
  Playing,
  Complete,
}

export const AudioTestDialog: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isMountedRef = useIsMountedRef();
  const [recordingState, setRecordingState] = useState<RecordingState>(
    RecordingState.NotStarted
  );
  const [report, setReport] = useState<AudioInputTest.Report>();
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioSrcRef = useRef<HTMLSourceElement>(null);
  const { audioTrackInfo, localAudioTrack, audioOutput } = useSelector(
    (s: RootState) => s.localTracks
  );
  const startAudioTest = useCallback(
    () => {
      setRecordingState(RecordingState.InProgress);
      const audioInputDeviceTest = testAudioInputDevice({
        enableRecording: true,
        deviceId: audioTrackInfo?.deviceId,
      });

      audioInputDeviceTest.on(AudioInputTest.Events.Error, (error) => {
        console.error(error);
      });

      audioInputDeviceTest.on(AudioInputTest.Events.End, (report) => {
        if (isMountedRef.current) {
          setReport(report);

          if (report.recordingUrl) {
            setRecordingState(RecordingState.Playing);

            if (audioRef.current && audioSrcRef.current) {
              audioRef.current.onended = () => {
                if (isMountedRef.current) {
                  setRecordingState(RecordingState.Complete);
                }
                URL.revokeObjectURL(report.recordingUrl || "");
              };

              if ((audioRef.current as any)?.setSinkId) {
                (audioRef.current as any).setSinkId(audioOutput?.deviceId);
              }

              audioSrcRef.current.src = report.recordingUrl;
              audioRef.current.load();
              audioRef.current.play();
            }
          } else {
            setRecordingState(RecordingState.Complete);
          }
        }
      });

      setTimeout(() => {
        audioInputDeviceTest.stop();
      }, 3000);
    },
    [audioTrackInfo, audioOutput, isMountedRef]
  );

  return (
    <div className={styles["container"]}>
      <div className={styles["dialog"]}>
        <div className="mb-4 pl-4 pr-4">
          <p className="lead text-center mb-0">Microphone Test</p>
        </div>
        <p className="text-center">
          {recordingState === RecordingState.NotStarted && (
            <>
              Click the <code>Test Mic</code> button to record your microphone
              for a few seconds. We'll play back the recording for you.
            </>
          )}
          {recordingState === RecordingState.InProgress && (
            <em>Recording...</em>
          )}
          {recordingState === RecordingState.Playing && (
            <em>Playing back recording...</em>
          )}
          {recordingState === RecordingState.Complete && (
            <strong>Test complete.</strong>
          )}
        </p>
        {recordingState === RecordingState.Complete && (
          <>
            {report?.errors.length ? (
              <>
                <p>There were errors encountered while recording your audio:</p>
                <ul>
                  {report.errors.map((e) => {
                    if (e.domError) {
                      return (
                        <li>
                          {e.domError.name}: {(e.domError as any).message}
                        </li>
                      );
                    } else {
                      return (
                        <li>
                          {e.name}: {e.message}
                        </li>
                      );
                    }
                  })}
                </ul>
              </>
            ) : (
              <p>No problems were detected with your microphone.</p>
            )}
          </>
        )}

        {recordingState === RecordingState.InProgress && (
          <>
            <div className={`${styles["icon-wrapper"]} mb-3`}>
              {localAudioTrack && (
                <AudioLevelMeter audioTrack={localAudioTrack} />
              )}
              <Icon id={"microphone"} />
            </div>
          </>
        )}
        {recordingState === RecordingState.Playing && (
          <>
            <div className={`${styles["icon-wrapper"]} mb-3`}>
              <Icon id={"speaker"} />
            </div>
          </>
        )}
        {(recordingState === RecordingState.NotStarted ||
          recordingState === RecordingState.Complete) && (
          <button
            type="submit"
            className={`btn btn-lg btn-primary mb-3 ${styles["test-btn"]}`}
            onClick={startAudioTest}
          >
            <Icon id={"microphone"} /> Test Mic
          </button>
        )}

        <button
          type="submit"
          className="btn btn-outline-primary btn-block"
          onClick={() => dispatch(toggleAudioTestDialog())}
        >
          Close
        </button>
        <audio id="audio-input-test" ref={audioRef}>
          <source ref={audioSrcRef} src=""></source>
        </audio>
      </div>
    </div>
  );
};
